import {
  BottomSheetBackdropProps,
  BottomSheetModal,
} from '@gorhom/bottom-sheet';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler } from 'react-native';

import { Alert } from '@components/Alert';
import { BottomSheetBackdrop } from '@components/Modals/BottomSheetBackdrop';

interface ChildModal {
  isOpen: boolean;
  dismiss: () => void;
}

// Stable default so callers that omit `childModals` don't re-register the
// Android back handler on every render.
const NO_CHILD_MODALS: ChildModal[] = [];

interface BottomSheetDiscardBaseOptions {
  bottomSheetRef: React.RefObject<BottomSheetModal>;
  noNeedShowDiscard: boolean;
  isFocused: boolean;
  childModals?: ChildModal[];
}

interface ConfirmingDiscardOptions extends BottomSheetDiscardBaseOptions {
  confirmDiscard?: true;
  isBlankForm: boolean;
  onDismiss: () => void;
  titleKey: string;
  messageKey: string;
}

// TA-10733: a flow that never confirms (task New Update) has no use for the
// alert copy, the dirty-form flag or the post-discard callback — nothing reads
// them — so the type refuses them rather than letting them read as live wiring.
interface NonConfirmingDiscardOptions extends BottomSheetDiscardBaseOptions {
  confirmDiscard: false;
  isBlankForm?: never;
  onDismiss?: never;
  titleKey?: never;
  messageKey?: never;
}

type UseBottomSheetDiscardOptions =
  | ConfirmingDiscardOptions
  | NonConfirmingDiscardOptions;

/**
 * Manages discard confirmation and Android back button handling for BottomSheet forms.
 * Extracts the shared pattern from TaskCreateBottomSheet, ProjectCreateBottomSheet,
 * and ProjectTaskBottomSheet.
 */
export const useBottomSheetDiscard = ({
  bottomSheetRef,
  isBlankForm,
  noNeedShowDiscard,
  isFocused,
  onDismiss,
  titleKey,
  messageKey,
  childModals = NO_CHILD_MODALS,
  confirmDiscard = true,
}: UseBottomSheetDiscardOptions) => {
  const { t } = useTranslation();

  const discardAlert = useCallback(() => {
    // Only reachable on the confirming branch of the options union, where the
    // copy is required; the guard is what makes that safe without a cast.
    if (!titleKey || !messageKey) return;

    Alert.alert(t(titleKey), t(messageKey), [
      {
        text: t('shared:cancel'),
        onPress: () => {
          bottomSheetRef.current?.present();
        },
      },
      {
        text: t('shared:discard'),
        style: 'destructive',
        onPress: () => {
          bottomSheetRef.current?.dismiss();
          onDismiss?.();
        },
      },
    ]);
  }, [t, titleKey, messageKey, onDismiss, bottomSheetRef]);

  const needsConfirmation = confirmDiscard && !isBlankForm;

  useEffect(() => {
    if (!isFocused) return;

    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        for (const modal of childModals) {
          if (modal.isOpen) {
            modal.dismiss();
            return true;
          }
        }

        if (noNeedShowDiscard) return false;

        needsConfirmation ? discardAlert() : bottomSheetRef.current?.dismiss();
        return true;
      }
    );

    return () => backHandler.remove();
  }, [
    isFocused,
    needsConfirmation,
    noNeedShowDiscard,
    childModals,
    bottomSheetRef,
    discardAlert,
  ]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        closeSheet={() =>
          needsConfirmation ? discardAlert() : bottomSheetRef.current?.dismiss()
        }
      />
    ),
    [needsConfirmation, bottomSheetRef, discardAlert]
  );

  return { discardAlert, renderBackdrop };
};
